import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { RootState } from 'redux/store';
import { apiEditor } from 'services/api/api';
import { EditorApiEndpoints } from 'utils/endpoints/endpoints';
import {
  IGetEmpresasClienteParams,
  IGetMenorCompetenciaParams,
  IObterProcessamentosEditaveisParams,
} from 'utils/endpoints/requests';
import { formatCnpjCpf } from 'utils/formatUtils';
import { appendQueryParams } from 'utils/requestUtils';
import { alertDefaultConfig } from 'utils/toastifyUtils';
import {
  EtapaParalisacao,
  etapaParalisacaoOptions,
  IEmpresaCliente,
  IOptions,
  Paginador,
  parseEtapaParalisacaoToQueryParam,
} from 'utils/types';
import { IArquivoEditavel } from 'utils/types/arquivoEditavel';

import { Grid2 as Grid } from '@mui/material';

import BackdropLoading from 'components/BackdropLoading';
import CardMainContainer from 'components/CardMainContainer';
import CustomAutocompleteMultiple from 'components/CustomAutocompleteMultiple';
import CustomSelectMultiple from 'components/CustomSelectMultiple';
import DataTableArquivosEditaveis from 'components/DataTableArquivosEditaveis';
import MonthPicker from 'components/MonthPicker';

import { IArquivosEditaveisFilter } from './filters';
import {
  ButtonFilterContainer,
  ClearFilterButton,
  FilterButton,
} from './SpedFilesList.styles';

const SpedFilesList = () => {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [empresasClientesOptions, setEmpresasClientesOptions] = useState<
    IOptions[]
  >([]);
  const [isFiltroAplicado, setIsFiltroAplicado] = useState(0);

  const { contratante: empresaContratante, id: usuarioID } = useSelector(
    (state: RootState) => state.token.usuario,
  );

  const formik = useFormik<IArquivosEditaveisFilter>({
    initialValues: {
      empresaClienteIDs: [],
      competencia: dayjs().subtract(1, 'month'),
      etapasParalisacao: [],
    },
    onSubmit: () => {
      refetchArquivosEditaveis();
    },
    validateOnBlur: false,
    validateOnChange: false,
  });

  const fetchEmpresasClientes = useCallback(async () => {
    const queryParams: IGetEmpresasClienteParams = {
      contratanteID: empresaContratante.id,
    };
    const response = await apiEditor.get<IEmpresaCliente[]>(
      appendQueryParams(EditorApiEndpoints.GetEmpresasCliente, queryParams),
    );
    if (response.status === 204) {
      return [];
    }

    return response.data;
  }, [empresaContratante.id]);

  const {
    data: empresasClientes,
    isPending: isPendingEmpresasClientes,
    isError: isErrorEmpresasClientes,
  } = useQuery({
    queryKey: ['empresasClientes', empresaContratante.id],
    queryFn: fetchEmpresasClientes,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const handleClearFilters = () => {
    formik.setValues({
      empresaClienteIDs: [],
      competencia: dayjs().subtract(1, 'month'),
      etapasParalisacao: [],
    });
    setIsFiltroAplicado((prev) => prev + 1);
  };

  useEffect(() => {
    if (isErrorEmpresasClientes) {
      toast.error('Erro ao consultar empresas clientes.', alertDefaultConfig);
    }
  }, [isErrorEmpresasClientes]);

  useEffect(() => {
    if (!!empresasClientes?.length) {
      const options: IOptions[] = empresasClientes.map(
        (empresaCliente: IEmpresaCliente) => ({
          label: `${formatCnpjCpf(empresaCliente.cnpjCpf)} ${!!empresaCliente.scp ? `- SCP: ${empresaCliente.scp}` : ''} ${!!empresaCliente.inscricaoEstadual ? `- IE: ${empresaCliente.inscricaoEstadual}` : ''} - ${empresaCliente.nome}`,
          value: empresaCliente.id,
        }),
      );
      setEmpresasClientesOptions(options);
    }
  }, [empresasClientes]);

  const fetchMenorCompetencia = useCallback(async () => {
    const queryParams: IGetMenorCompetenciaParams = {
      contratanteID: empresaContratante.id,
      usuarioID: usuarioID,
      isEmpresaClienteAtiva: true,
    };
    const response = await apiEditor.get(
      appendQueryParams(EditorApiEndpoints.GetMenorCompetencia, queryParams),
    );

    return dayjs(response.data);
  }, [empresaContratante, usuarioID]);

  const {
    data: menorCompetencia,
    isPending: isPendingMenorCompetencia,
    isError: isErrorMenorCompetencia,
  } = useQuery({
    queryKey: ['menorCompetencia'],
    queryFn: fetchMenorCompetencia,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    if (isErrorMenorCompetencia) {
      toast.error('Erro ao consultar menor competência.', alertDefaultConfig);
    }
  }, [isErrorMenorCompetencia]);

  const fetchArquivosEditaveis = useCallback(async () => {
    const queryParams: IObterProcessamentosEditaveisParams = {
      usuarioID: usuarioID,
      empresaContratanteID: empresaContratante.id,
      empresaClienteIDs: formik.values.empresaClienteIDs?.map(
        (option: IOptions) => option.value,
      ),
      competencia: formik.values.competencia
        .set('date', 1)
        .format('YYYY-MM-DD'),
      etapasParalisacao: formik.values.etapasParalisacao?.map(
        (etapaParalisacao: EtapaParalisacao) =>
          parseEtapaParalisacaoToQueryParam(etapaParalisacao),
      ),
      Pagina: page,
      QuantidadeNaPagina: rowsPerPage,
    };
    const response = await apiEditor.get<Paginador<IArquivoEditavel>>(
      appendQueryParams(
        EditorApiEndpoints.ObterProcessamentosEditaveis,
        queryParams,
      ),
    );

    if (response.status === 204) {
      return {
        conteudo: [],
        totalConteudo: 0,
        pagina: 1,
        quantidadeNaPagina: rowsPerPage,
      };
    }

    return response.data;
  }, [usuarioID, empresaContratante.id, formik.values, page, rowsPerPage]);

  const {
    data: arquivosEditaveisPaginados,
    isPending: isPendingArquivosEditaveis,
    refetch: refetchArquivosEditaveis,
    isError: isErrorArquivosEditaveis,
  } = useQuery({
    queryKey: [
      'arquivosEditaveisPaginados',
      page,
      rowsPerPage,
      isFiltroAplicado,
    ],
    queryFn: fetchArquivosEditaveis,
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    if (isErrorArquivosEditaveis) {
      toast.error('Erro ao consultar arquivos editáveis.', alertDefaultConfig);
    }
  }, [isErrorArquivosEditaveis]);

  useEffect(() => {
    setPage(1);
  }, [isFiltroAplicado]);

  const isLoadingArquivos =
    isPendingArquivosEditaveis ||
    isPendingEmpresasClientes ||
    isPendingMenorCompetencia;

  return (
    <>
      {isLoadingArquivos && <BackdropLoading />}
      <CardMainContainer title="Busca de Arquivos">
        <form onSubmit={formik.handleSubmit}>
          <Grid
            container
            spacing={5}
            mb={8}
            sx={{ display: 'flex', alignItems: 'flex-end' }}
          >
            <Grid size={4}>
              <CustomAutocompleteMultiple
                name="empresaClienteIDs"
                label="Empresas"
                multiple
                options={empresasClientesOptions || []}
                id={'autocomplete_with_select_all'}
                value={formik.values.empresaClienteIDs}
                fullWidth
                setValue={(value) => {
                  formik.setFieldValue('empresaClienteIDs', value);
                }}
                disableCloseOnSelect
                freeSolo={false}
              />
            </Grid>

            <Grid size={3}>
              <MonthPicker
                startDate={menorCompetencia || dayjs()}
                endDate={dayjs()}
                label="Competência"
                name="competencia"
                setValue={(value) => formik.setFieldValue('competencia', value)}
                value={formik.values.competencia}
              />
            </Grid>

            <Grid size={2}>
              <CustomSelectMultiple
                id="etapasParalisacao"
                name="etapasParalisacao"
                label="Etapa de Paralisação"
                options={etapaParalisacaoOptions || []}
                setValue={(value) => {
                  formik.setFieldValue('etapasParalisacao', value);
                }}
                value={formik.values.etapasParalisacao}
              />
            </Grid>

            <ButtonFilterContainer size={3}>
              <FilterButton
                variant="contained"
                color="primary"
                onClick={() => {
                  setIsFiltroAplicado((prev) => prev + 1);
                }}
              >
                Filtrar
              </FilterButton>
              <ClearFilterButton
                color="secondary"
                size="medium"
                disableRipple
                disableTouchRipple
                disableFocusRipple
                onClick={handleClearFilters}
              >
                Limpar filtros
              </ClearFilterButton>
            </ButtonFilterContainer>
          </Grid>
        </form>

        <DataTableArquivosEditaveis
          data={arquivosEditaveisPaginados || []}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
        />
      </CardMainContainer>
    </>
  );
};

export default SpedFilesList;
